import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import { ResumeDetails } from '../../redux/Slices/ResumeSlice/types';
import { useUpdateResumeMutation } from '../../services/resumeBuilderApi';
import Button from '../Common/Button';

import ResumeEditor from './ResumeEditor/ResumeEditor';

type Props = {
  onSubmit: () => void;
  handleBack: () => void;
};

const ResumePreviewLayout: React.FC<Props> = ({ onSubmit, handleBack }) => {
  const resumeTemplet = useSelector(
    (state: { root: { resume: ResumeDetails } }) => state.root.resume
  );
  const atsMetadata = resumeTemplet?.atsMetadata;
  const score = atsMetadata?.estimatedAtsScore ?? 'N/A';

  const [updateResume] = useUpdateResumeMutation();
  const resumeRef = useRef(resumeTemplet);
  resumeRef.current = resumeTemplet;

  const handleSoftSave = useCallback(
    async (html: string) => {
      const current = resumeRef.current as any;
      if (!current?.id) return;

      try {
        await updateResume({
          ...current,
          renderedHtml: html
        }).unwrap();
      } catch (err) {
        console.error('Soft save failed:', err);
      }
    },
    [updateResume]
  );

  // Convert the resume title into a safe filename (fallback to "resume")
  const getFileName = (ext: string) => {
    const raw = (resumeTemplet?.title || '').trim();
    const safe = raw
      .replace(/[\\/:*?"<>|]+/g, '') // strip filesystem-reserved chars
      .replace(/\s+/g, '_'); // spaces → underscores
    return `${safe || 'resume'}.${ext}`;
  };

  const handleDownloadPdf = async () => {
    const iframe = document.getElementById(
      'resume-iframe'
    ) as HTMLIFrameElement | null;
    const source =
      iframe?.contentWindow?.document?.body ||
      document.getElementById('resume-root');

    if (!source) {
      console.warn('Resume content not found for PDF export.');
      return;
    }

    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: 0.4,
        filename: getFileName('pdf'),
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };
      html2pdf().set(opt).from(source).save();
    } catch (err) {
      console.error('html2pdf failed, falling back to print()', err);
      handlePrint();
    }
  };

  // ---- Export to Word (.doc via HTML blob) --------------------------------
  const handleExportWord = () => {
    const iframe = document.getElementById(
      'resume-iframe'
    ) as HTMLIFrameElement | null;
    const html = iframe?.contentWindow?.document?.body?.innerHTML || '';
    const header =
      // eslint-disable-next-line quotes
      "<html xmlns:o='urn:schemas-microsoft-com:office:office' " +
      // eslint-disable-next-line quotes
      "xmlns:w='urn:schemas-microsoft-com:office:word' " +
      // eslint-disable-next-line quotes
      "xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>Resume</title></head><body>";
    const footer = '</body></html>';
    const blob = new Blob(['\ufeff', header + html + footer], {
      type: 'application/msword'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = getFileName('doc');
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const iframe = document.getElementById(
      'resume-iframe'
    ) as HTMLIFrameElement | null;
    if (iframe?.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } else {
      window.print();
    }
  };
  return (
    <div className='flex min-h-screen flex-col items-start gap-6 bg-gray-50 p-6 lg:flex-row'>
      {/* LEFT — Editor */}
      <div
        id='resume-root'
        className='w-full rounded-xl bg-white p-4 shadow-sm'
      >
        {resumeTemplet?.renderedHtml && (
          <ResumeEditor
            htmlTemplate={resumeTemplet?.renderedHtml}
            onSoftSave={handleSoftSave}
          />
        )}
      </div>

      {/* RIGHT — Sidebar */}
      <aside className='flex w-full flex-col gap-4'>
        <div className='mt-8 flex justify-center gap-4'>
          <Button
            label='Back'
            handleClickBtn={() => handleBack()}
            variant='outlined'
          />
          <Button
            label='Done'
            handleClickBtn={() => onSubmit()}
            variant='contained'
          />
        </div>

        <div className='rounded-xl border border-gray-100 bg-white p-5 shadow-sm'>
          <h3 className='m-0 mb-4 text-[15px] font-semibold text-gray-900'>
            Export Options
          </h3>
          <Button
            label='Download PDF'
            handleClickBtn={() => handleDownloadPdf()}
            variant='outlined'
          >
            {' '}
            <span className='mr-3 flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-sm text-violet-700'>
              📄
            </span>
          </Button>
          <Button
            label='Export to Word'
            handleClickBtn={() => handleExportWord()}
            variant='outlined'
            classes='mt-3'
          >
            <span className='mr-3 flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-sm text-blue-700'>
              📝
            </span>
          </Button>
        </div>

        <div className='relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-700 to-violet-800 p-6 text-white shadow-lg shadow-violet-500/25'>
          <p className='m-0 text-[15px] font-semibold opacity-95'>ATS Score</p>
          <div className='mt-0.5 text-[44px] font-bold leading-tight'>
            {score}
          </div>
          <p className='mt-2 text-[13px] leading-relaxed opacity-90'>
            {atsMetadata?.atsScoreJustification ||
              'This resume is optimized for ATS (Applicant Tracking Systems). Youre ready to apply!'}
          </p>
        </div>
      </aside>
    </div>
  );
};

export default ResumePreviewLayout;
